import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  StatCard,
} from '@/components/ui';
import { formatDateTime, formatMoney, formatNumber } from '@/lib/format';

/**
 * Call history, across every client.
 *
 * The Live calls board answers "what is happening right now" and holds only
 * live states. This is the record — and until it existed, the only way for an
 * operator to look at a finished call on somebody else's account was to open
 * their portal as them.
 *
 * The client column is the whole difference from the client's own Calls page.
 * Everything else is deliberately the same, so an operator reading a support
 * ticket is looking at what the client is looking at.
 */

interface CallRow {
  id: string;
  tenant_id: string;
  client_name: string;
  state: string;
  disposition: string | null;
  caller_number: string | null;
  vicidial_user: string | null;
  vicidial_campaign: string | null;
  vicidial_lead_id: string | null;
  qualified: boolean;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  duration_sec: number | null;
  bot_name: string | null;
  dialer_name: string | null;
  total_client: string | null;
  total_actual: string | null;
}

interface Response {
  data: CallRow[];
  total: number;
  page: number;
  page_size: number;
  window_days: number;
  summary: {
    calls: number;
    qualified: number;
    transferred: number;
    avg_seconds: number;
    total_seconds: number;
  };
  outcome_labels: Record<string, string>;
}

interface ClientOption {
  id: string;
  name: string;
}

const STATES = [
  'AI_ACTIVE',
  'QUALIFYING',
  'QUALIFIED',
  'TRANSFER_REQUESTED',
  'HUMAN_CONNECTED',
  'NOT_INTERESTED',
  'DNC',
  'CALLBACK',
  'NO_ANSWER',
  'BUSY',
  'FAILED',
  'ENDED',
];

const LIVE = new Set([
  'AI_ACTIVE',
  'QUALIFYING',
  'QUALIFIED',
  'TRANSFER_REQUESTED',
  'TRANSFER_RINGING',
  'HUMAN_CONNECTED',
]);

/** Seconds as m:ss — "137s" is not how anybody reads a call length. */
function duration(seconds: number | null): string {
  if (!seconds) return '—';
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export default function AdminCalls() {
  const [days, setDays] = useState(7);
  const [tenantId, setTenantId] = useState('');
  const [state, setState] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const clients = useQuery({
    queryKey: ['admin-clients-select'],
    queryFn: () => api.get<{ data: ClientOption[] }>('/admin/clients', { page_size: 200 }),
    staleTime: 60_000,
  });

  const calls = useQuery({
    queryKey: ['admin-calls', days, tenantId, state, q, page],
    queryFn: () =>
      api.get<Response>('/admin/calls', {
        days,
        page,
        tenant_id: tenantId || undefined,
        state: state || undefined,
        q: q || undefined,
      }),
  });

  const summary = calls.data?.summary;
  const pages = calls.data ? Math.ceil(calls.data.total / calls.data.page_size) : 1;

  return (
    <div className="w-full">
      <PageHeader
        title="Call history"
        description="Every call on the platform, whoever it belongs to."
        actions={
          <div className="flex gap-2">
            <Select
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setPage(1);
              }}
              className="w-auto"
            >
              <option value="">All clients</option>
              {(clients.data?.data ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
            <Select
              value={days}
              onChange={(e) => {
                setDays(Number(e.target.value));
                setPage(1);
              }}
              className="w-auto"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </Select>
          </div>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Calls" value={formatNumber(summary.calls)} />
          <StatCard
            label="Transferred"
            value={formatNumber(summary.transferred)}
            sub={
              summary.calls
                ? `${((summary.transferred / summary.calls) * 100).toFixed(1)}% of calls`
                : undefined
            }
          />
          <StatCard label="Qualified" value={formatNumber(summary.qualified)} />
          <StatCard
            label="Average length"
            value={duration(summary.avg_seconds)}
            sub={`${formatNumber(Math.round(summary.total_seconds / 60))} minutes total`}
          />
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Field label="State">
          <Select
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Any state</option>
            {STATES.map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, ' ').toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Caller or lead" hint="Phone number or Vicidial lead id.">
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="863410…"
          />
        </Field>
      </div>

      <Card className="mt-4" padded={false}>
        {calls.isPending ? (
          <Loading />
        ) : calls.error ? (
          <ErrorState error={calls.error} retry={() => calls.refetch()} />
        ) : !calls.data.data.length ? (
          <EmptyState
            title="No calls in this window"
            description="Widen the date range, or clear the filters."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="glee-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Client</th>
                  <th>Caller</th>
                  <th>Bot</th>
                  <th>State</th>
                  <th>Outcome</th>
                  <th className="text-right">Length</th>
                  <th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {calls.data.data.map((call) => (
                  <tr key={call.id}>
                    <td className="whitespace-nowrap text-xs">
                      {/* The row leads somewhere now. Until the detail page
                          existed this list was the end of the road, and an
                          operator reading a ticket had to open the client's
                          own portal as them to see the transcript. */}
                      <Link
                        to={`/calls/${call.id}`}
                        className="text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {formatDateTime(call.started_at)}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/clients/${call.tenant_id}`}
                        className="text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {call.client_name}
                      </Link>
                    </td>
                    <td>
                      <div className="font-mono text-xs">{call.caller_number ?? '—'}</div>
                      {call.vicidial_lead_id && (
                        <div className="text-2xs text-ink-500">
                          lead {call.vicidial_lead_id}
                        </div>
                      )}
                    </td>
                    <td className="text-xs">
                      {call.bot_name ?? '—'}
                      {call.vicidial_campaign && (
                        <div className="text-2xs text-ink-500">
                          {call.vicidial_campaign}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge tone={LIVE.has(call.state) ? 'green' : 'gray'}>
                        {call.state.replace(/_/g, ' ').toLowerCase()}
                      </Badge>
                    </td>
                    <td className="text-xs">
                      {call.disposition ?? '—'}
                      {call.end_reason && (
                        <div className="text-2xs text-ink-500">{call.end_reason}</div>
                      )}
                    </td>
                    <td className="text-right tabular-nums">
                      {duration(call.duration_sec)}
                    </td>
                    <td className="text-right tabular-nums text-xs">
                      {call.total_actual ? formatMoney(Number(call.total_actual)) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {calls.data && pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-ink-500">
            Page {page} of {pages} · {formatNumber(calls.data.total)} calls
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
